import { NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useIdentity } from '../lib/identity';
import NotificationBell from './NotificationBell';
import { PRIMARY_NAV, MORE_ICON, visibleItems, isMorePath } from './navItems';

export default function Layout({ children }: { children: ReactNode }) {
  const identity = useIdentity();
  const { pathname } = useLocation();
  const items = visibleItems(PRIMARY_NAV, identity.role);
  const moreActive = isMorePath(pathname);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="sticky top-0 z-20 border-b border-border bg-canvas/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-4 py-3">
          <NavLink to="/" className="font-display text-lg font-semibold whitespace-nowrap text-ink">
            Mangum Home
          </NavLink>

          {/* Desktop nav mirrors the mobile bar: same five tabs + More, so nothing
              is reachable on one breakpoint but missing on the other. */}
          <nav className="hidden flex-1 gap-1 lg:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-sage-soft font-medium text-sage'
                      : 'text-ink-soft hover:bg-surface-sunk hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/more"
              className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                moreActive
                  ? 'bg-sage-soft font-medium text-sage'
                  : 'text-ink-soft hover:bg-surface-sunk hover:text-ink'
              }`}
            >
              More
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-2 lg:ml-0">
            <NotificationBell />
            {/* The avatar is the way into your own profile — it's where everyone looks. */}
            <NavLink
              to="/profile"
              className="grid h-8 w-8 place-items-center rounded-full text-xs font-semibold text-white ring-offset-2 ring-offset-canvas transition-shadow hover:ring-2 focus-visible:ring-2"
              style={{ backgroundColor: identity.color ?? 'var(--color-ink-faint)' }}
              aria-label="Your profile"
              title={identity.name ?? identity.email}
            >
              {(identity.name ?? identity.email).slice(0, 1).toUpperCase()}
            </NavLink>
          </div>
        </div>
      </header>

      {/* pb-28 on mobile keeps content clear of the fixed bottom bar */}
      <main className="mx-auto max-w-6xl px-4 py-8 pb-28 lg:pb-8">{children}</main>

      <nav
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-canvas/95 backdrop-blur lg:hidden"
        aria-label="Main"
      >
        <div className="grid auto-cols-fr grid-flow-col px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-lg py-1 text-xs transition-colors ${
                  isActive ? 'font-semibold text-sage' : 'font-medium text-ink-soft'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`grid h-7 w-14 place-items-center rounded-full transition-colors ${
                      isActive ? 'bg-sage-soft' : ''
                    }`}
                    aria-hidden="true"
                  >
                    <span className="h-6 w-6">{item.icon}</span>
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
          <NavLink
            to="/more"
            className={`flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-lg py-1 text-xs transition-colors ${
              moreActive ? 'font-semibold text-sage' : 'font-medium text-ink-soft'
            }`}
          >
            <span
              className={`grid h-7 w-14 place-items-center rounded-full transition-colors ${
                moreActive ? 'bg-sage-soft' : ''
              }`}
              aria-hidden="true"
            >
              <span className="h-6 w-6">{MORE_ICON}</span>
            </span>
            More
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
